import dayjs from "dayjs";
import 'dayjs/locale/es';
import React from "react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import styled from "styled-components";
import { ScoreItem } from "../../../services/reportsRecapGet";

dayjs.locale('es');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 24px;
  background: linear-gradient(135deg, #abddffff 0%, #ffffff 50%, #abddffff 100%);
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 32px;
`;

const StatsGrid = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  margin-bottom: 32px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: #5d636eff;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const StatValue = styled.span<{ color?: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.color || "#111827"};
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 250px;
`;

interface ScoreEvolutionSlideProps {
  history: ScoreItem[];
}

const EmptyIcon = styled.div`
  font-size: 80px;
  margin-bottom: 24px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 16px;
`;
const EmSubtitle = styled.h3`
  color: #2563eb;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
  padding: 8px 16px;
  background: #eff6ff;
  border-radius: 20px;
  display: inline-block;
`;

const ScoreEvolutionSlide: React.FC<ScoreEvolutionSlideProps> = ({ history }) => {
  if ((!history || history.length === 0)) {
    return (
      <Container>
        <EmptyContainer>
          <Title>Tu Evolución</Title>
          <EmptyIcon>🚀</EmptyIcon>
          <EmSubtitle>Despega</EmSubtitle>
          <Subtitle style={{ maxWidth: 300, lineHeight: 1.5 }}>
            Has estado cerca, pero aún no conoces tu puntaje inicial en Enbanca. <br /><br /><span style={{ fontWeight: 700 }}>¡Pero puedes mejorar!</span> Activa tu plan hoy y descubre tu punto de partida para un 2026 exitoso.
          </Subtitle>
        </EmptyContainer>
      </Container>
    );
  }

  const sortedHistory = [...history].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

  const initialScore = sortedHistory[0].puntaje;
  const finalScore = sortedHistory[sortedHistory.length - 1].puntaje;
  const change = finalScore - initialScore;

  const data = sortedHistory.map(item => ({
    name: dayjs(item.fecha).format("MMM"),
    score: item.puntaje
  }));

  const isSinglePoint = sortedHistory.length === 1;
  const isPositive = change >= 0;

  let description = "";
  if (isSinglePoint) {
    description = "¡Este es tu punto de partida! Tienes todo el 2026 para construir un gran historial financiero.";
  } else if (change > 0) {
    description = "Tu score creció este año. Cada mejora refleja tus esfuerzos. Los buenos hábitos dieron resultados";
  } else if (change === 0) {
    description = "La estabilidad también habla de buenos hábitos. Revisa que acciones tuviste este año, replica y mejóralas para el 2026 ¡Vas muy bien!";
  } else {
    description = "Los puntajes pueden variar, pero tu decisión de monitorearlo ya es un avance. Lo importante es que estás informado y listo para mejorar";
  }

  return (
    <Container>
      <Title>Tu Evolución</Title>
      <Subtitle>{description}</Subtitle>

      {!isSinglePoint ? (
        <StatsGrid>
          <Stat>
            <StatLabel>Inicial</StatLabel>
            <StatValue>{initialScore}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>Variación</StatLabel>
            <StatValue color={isPositive ? "#059669" : "#d97706"}>
              {isPositive ? "+" : ""}{change}
            </StatValue>
          </Stat>
          <Stat>
            <StatLabel>Final</StatLabel>
            <StatValue color="#2563eb">{finalScore}</StatValue>
          </Stat>
        </StatsGrid>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '40px 0' }}>
          <Stat>
            <StatLabel>Tu Score Actual</StatLabel>
            <StatValue color="#2563eb" style={{ fontSize: '64px' }}>{finalScore}</StatValue>
          </Stat>
          <div style={{ marginTop: 16, padding: '8px 16px', background: '#f1f5f9', borderRadius: 8, color: '#64748b', fontSize: 14 }}>
            Registrado en {dayjs(sortedHistory[0].fecha).format("MMMM")}
          </div>
        </div>
      )}

      {sortedHistory.length > 1 && (
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <YAxis hide domain={['dataMin - 50', 'dataMax + 50']} />
              <Tooltip
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#2563eb"
                strokeWidth={3}
                dot={{ fill: '#2563eb', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </Container>
  );
};

export default ScoreEvolutionSlide;
